import { PRICE_PATTERN, type ComposeRewriteAction } from '@fieldops/shared'
import { useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode, type RefObject } from 'react'
import { cn } from '../../lib/cn.js'
import { Icon } from '../Icon.js'
import { EcoMark } from '../eco/EcoAvatar.js'

export interface TextRange {
  start: number
  end: number
}

/*
 * The mirror and the textarea must lay text out identically, or the
 * highlight drifts off the words it is meant to sit under.
 */
const TEXT = 'm-0 w-full border-0 p-0 font-sans text-[15px] leading-[1.65] whitespace-pre-wrap break-words'

const MORE: Array<{ action: ComposeRewriteAction; label: string }> = [
  { action: 'friendlier', label: 'Friendlier' },
  { action: 'formal', label: 'More formal' },
  { action: 'fix', label: 'Fix spelling' },
]

/**
 * The message, as plain text, with a highlight that survives losing focus.
 *
 * A textarea cannot draw a highlight of its own once the toolbar is clicked,
 * so a mirror behind it lays out the same words with a `<mark>` over the
 * chosen range. The mark is also what the toolbar measures to sit above the
 * words. The textarea grows with its text and the pane scrolls, so the two
 * never need their scroll positions kept in step.
 */
export function MessageBody({
  id,
  label,
  value,
  placeholder,
  range,
  busy,
  readOnly,
  canRewrite,
  textareaRef,
  onType,
  onRange,
  onRewrite,
  onUndo,
  onRedo,
  onSubmit,
}: {
  id: string
  label: string
  value: string
  placeholder?: string
  range: TextRange | null
  /** The range being rewritten, which shimmers until the words come back. */
  busy: TextRange | null
  readOnly?: boolean
  canRewrite: boolean
  textareaRef?: RefObject<HTMLTextAreaElement | null>
  onType: (value: string) => void
  onRange: (range: TextRange | null) => void
  onRewrite: (action: ComposeRewriteAction, range: TextRange) => void
  onUndo: () => void
  onRedo: () => void
  onSubmit?: () => void
}) {
  const ownRef = useRef<HTMLTextAreaElement>(null)
  const area = textareaRef ?? ownRef
  const box = useRef<HTMLDivElement>(null)
  const mark = useRef<HTMLElement>(null)
  const [at, setAt] = useState<{ top: number; bottom: number; left: number; below: boolean } | null>(null)
  const [shift, setShift] = useState(0)
  const bar = useRef<HTMLDivElement>(null)
  const [more, setMore] = useState(false)

  const shown = busy ?? range
  const chosen = range && range.end > range.start ? value.slice(range.start, range.end) : ''
  const toolbar = canRewrite && !busy && !readOnly && chosen.trim().length > 1

  useLayoutEffect(() => {
    const element = area.current
    if (!element) return
    element.style.height = 'auto'
    element.style.height = `${element.scrollHeight}px`
  }, [value, area])

  useLayoutEffect(() => {
    if (!toolbar || !mark.current || !box.current) {
      setAt(null)
      return
    }
    const first = mark.current.getClientRects()[0] ?? mark.current.getBoundingClientRect()
    const frame = box.current.getBoundingClientRect()
    const top = first.top - frame.top
    setAt({ top, bottom: first.bottom - frame.top, left: Math.max(0, first.left - frame.left), below: top < 44 })
  }, [toolbar, range?.start, range?.end, value])

  // Keep the toolbar's right edge inside the body; on a phone it is wider than the text and hangs left instead.
  useLayoutEffect(() => {
    if (!at || !bar.current || !box.current) return
    setShift(Math.min(0, box.current.clientWidth - bar.current.offsetWidth - at.left))
  }, [at])

  useLayoutEffect(() => {
    if (!toolbar) setMore(false)
  }, [toolbar])

  const readRange = () => {
    const element = area.current
    if (!element) return
    const next = { start: element.selectionStart, end: element.selectionEnd }
    if (!range || range.start !== next.start || range.end !== next.end) onRange(next)
  }

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    const command = event.metaKey || event.ctrlKey
    if (command && event.key === 'Enter') {
      event.preventDefault()
      onSubmit?.()
      return
    }
    if (command && event.key.toLowerCase() === 'z') {
      event.preventDefault()
      if (event.shiftKey) onRedo()
      else onUndo()
      return
    }
    if (command && event.key.toLowerCase() === 'y') {
      event.preventDefault()
      onRedo()
    }
  }

  const act = (action: ComposeRewriteAction) => {
    if (range) onRewrite(action, range)
    setMore(false)
  }

  const before = shown ? value.slice(0, shown.start) : value
  const middle = shown ? value.slice(shown.start, shown.end) : ''
  const after = shown ? value.slice(shown.end) : ''

  return (
    <div ref={box} className="relative">
      <div aria-hidden="true" className={cn(TEXT, 'pointer-events-none absolute inset-0 text-transparent')}>
        {before}
        {middle ? (
          <mark
            ref={mark}
            className={cn(
              'rounded-[3px] text-transparent',
              busy ? 'animate-pulse bg-brand-300' : 'bg-brand-200',
            )}
          >
            {middle}
          </mark>
        ) : null}
        {after}
        {'\u200b'}
      </div>
      <textarea
        id={id}
        ref={area}
        aria-label={label}
        rows={6}
        value={value}
        placeholder={placeholder}
        readOnly={readOnly}
        className={cn(
          TEXT,
          'relative block min-h-40 resize-none overflow-hidden bg-transparent text-ink-900 outline-none placeholder:text-ink-400',
          'selection:bg-brand-200/70',
          readOnly && 'cursor-progress',
        )}
        onChange={(event) => {
          onType(event.target.value)
          onRange({ start: event.target.selectionStart, end: event.target.selectionEnd })
        }}
        onSelect={readRange}
        onKeyDown={onKeyDown}
      />

      {toolbar && at ? (
        <div
          ref={bar}
          role="toolbar"
          aria-label="Change the highlighted words"
          className="absolute z-10 flex items-center gap-0.5 rounded-lg bg-ink-900 p-0.5 text-[12.5px] leading-5 font-medium text-white shadow-lg"
          // Snug against the highlight, so it covers the gap above and not the line before.
          style={
            at.below
              ? { top: at.bottom + 4, left: at.left + shift }
              : { top: at.top - 3, left: at.left + shift, transform: 'translateY(-100%)' }
          }
          onMouseDown={(event) => event.preventDefault()}
        >
          <ToolbarButton onClick={() => act('rewrite')}>
            <EcoMark className="size-4" />
            Rewrite
          </ToolbarButton>
          <ToolbarButton onClick={() => act('shorter')}>Shorter</ToolbarButton>
          <ToolbarButton onClick={() => act('simpler')}>Simpler</ToolbarButton>
          {PRICE_PATTERN.test(chosen) ? <ToolbarButton onClick={() => act('remove_price')}>Remove price</ToolbarButton> : null}
          <div className="relative">
            <ToolbarButton label="More changes" expanded={more} onClick={() => setMore((was) => !was)}>
              <Icon name="chevronDown" className="size-3.5" />
            </ToolbarButton>
            {more ? (
              <div role="menu" className="absolute top-full right-0 mt-1 w-40 rounded-lg bg-ink-900 p-1 shadow-lg">
                {MORE.map((item) => (
                  <button
                    key={item.action}
                    type="button"
                    role="menuitem"
                    className="block w-full rounded-md px-2.5 py-1.5 text-left hover:bg-white/10"
                    onClick={() => act(item.action)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  )
}

function ToolbarButton({
  label,
  expanded,
  onClick,
  children,
}: {
  label?: string
  expanded?: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-expanded={expanded}
      className="flex items-center gap-1.5 rounded-md px-2.5 py-0.5 whitespace-nowrap hover:bg-white/10"
      onClick={onClick}
    >
      {children}
    </button>
  )
}
